/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Decides which attributes distinguish one variant from another, and projects the set into table rows.
 *
 * The mirror image of `specifications.ts`. That module collapses the same variant set into one row per
 * attribute, describing the MODEL. This one keeps one row per variant, describing each ITEM — so the
 * attributes worth showing are the ones whose values DIFFER, exactly the opposite selection.
 *
 * An attribute holding one repeated value across the whole set earns no column: a column that reads
 * the same on every row distinguishes nothing and costs horizontal space the identity and commerce
 * columns need.
 *
 * **Columns are not read from the catalogue's declared variation attributes.** PIM declares those
 * inconsistently across products of the same family — one measured master declares three dimensions
 * and varies in three, another declares one and varies in six — and the failure is silent: a rule
 * reading declarations renders a table with no distinguishing columns on a model whose variants
 * plainly differ. What differs across the set is observable and needs no declaration to be correct.
 *
 * Consequently this module holds NO per-category list and needs no catalogue lookup. A category
 * nobody has configured still produces a correct table from its own variant set.
 */
import { readSpecificationValue, type SpecificationRow } from './specifications';
import { resolveSpecificationLabel } from './specification-labels';
import type { SpecificationAttribute } from './specification-attributes';
import type { ShopperProducts } from '@/scapi';

type Product = ShopperProducts.schemas['Product'];

/** Merchant attributes carry this prefix; system fields are not candidates for a column. */
const MERCHANT_ATTRIBUTE_PREFIX = 'c_';

/**
 * Stand-in for a price the catalogue cannot yet supply.
 *
 * An em dash, not a zero and not a blank: zero is a price and would be believed, and an empty cell
 * reads as a rendering fault. A dash says "no value here" in the one way a reader cannot mistake for
 * data.
 *
 * Held here rather than in either component because BOTH render it — the table for a price column it
 * cannot source, the order bar for a total it declines to state — and two components showing the same
 * absence must not be able to disagree about how it looks.
 */
export const PRICE_PLACEHOLDER = '—';

/**
 * One line of a pending order: the variant to add and how many.
 *
 * Deliberately no price. This is exactly what the basket action accepts, so the payload needs no
 * transform on its way out — and a price carried here would be a second copy of a figure the row
 * already holds, free to drift from it.
 */
export interface OrderLine {
    productId: string;
    quantity: number;
}

/** One attribute that distinguishes rows, with the values it takes across the set. */
export interface VariationColumn {
    /** The merchant attribute id, retained so consumers key and test on it rather than on labels. */
    attributeId: string;
    /** Display text, never a raw `c_` id. */
    label: string;
    /**
     * Distinct values in first-seen order. Kept as an ARRAY for the same reason specification rows
     * are: real values contain commas, so a joined string makes the boundaries unrecoverable.
     */
    values: string[];
}

/** One variant, with the values the table's columns read from it. */
export interface VariationRow {
    /** The variant itself, so a consumer can reach identity, inventory and price without a second map. */
    product: Product;
    /** Values keyed by attribute id; absent where that variant carries none. */
    values: Record<string, string | undefined>;
}

/** Everything the table needs, derived from the master's declaration and the variant set. */
export interface VariationsTableModel {
    /**
     * The attribute heading the table, which orders the rows. Absent when no declared attribute varies
     * across the set. It IS also offered as a filter — see {@link VariationsTableModel.filters}.
     */
    primary?: VariationColumn;
    /** The remaining varying attributes, each a column after the primary one. */
    secondary: VariationColumn[];
    /**
     * Every declared variation attribute the set carries a value for — the filter set.
     *
     * A SUPERSET of the columns, and deliberately not derived from them. Columns answer "what
     * distinguishes these rows"; filters answer "what dimensions does this model have", which the
     * master declares whether or not the resolved set happens to vary in them. So a declared attribute
     * with one value is a filter and not a column, and the primary dimension is both.
     *
     * Ordered as the master declares them, not as the rows happen to present them.
     */
    filters: VariationColumn[];
    /** One row per member of the set, in the order the resolver returned them. */
    rows: VariationRow[];
}

/**
 * One resolution of the Order band, read by both of its consumers.
 *
 * The table and the specifications panel render in different branches of the tree with their own
 * loading states, but they are ONE async operation: the same variant set, resolved once. Splitting it
 * into two promises would resolve the same data twice and let the two drift out of step.
 *
 * The attribute list travels alongside the rows it produced. `specificationRows` is the unfiltered
 * answer — correct on first paint and for every reader who never touches a filter — while
 * `specificationAttributes` is what lets the panel recompute against a filtered set in the browser,
 * with no further request.
 */
export interface ProductOrderData {
    /** The table's derived model: primary column, remaining columns, one row per variant. */
    model: VariationsTableModel;
    /**
     * The catalogue's specification attributes, in render order. Empty when none resolved.
     *
     * ONE list for both readers. The panel unions it across the whole set; an expanded row projects
     * the same list onto its single variant. The two differ in what they are given, not in what they
     * are allowed to show — which attributes suit which surface is a catalogue question, and the
     * answer will come from PIM by product type rather than from a second selection here.
     */
    specificationAttributes: SpecificationAttribute[];
    /** The specification union across the WHOLE set, before any filter narrows it. */
    specificationRows: SpecificationRow[];
    /**
     * Present only when the model has more variants than one page resolves.
     *
     * Carried to the client rather than logged and forgotten. A truncated set is a table that omits
     * rows a buyer could otherwise order, and a partial list presented as a complete one is the one
     * failure mode a reader cannot detect by looking — every row on screen is correct.
     */
    truncation?: { total: number; used: number };
    /**
     * Present when the variant set could not be resolved into something orderable.
     *
     * Carried to the client because the three causes are indistinguishable from the rows alone: a
     * network failure and a catalogue inconsistency both leave NO rows, and a master declaring no
     * variants leaves ONE — its own, which cannot be added to a basket. Rendering the table's ordinary
     * empty or single-row path for any of them offers a purchase control that cannot work.
     *
     * The reason does not change what the reader sees — there is nothing they can do about any of the
     * three but reload — so it travels for diagnosis rather than for display. The log line that names
     * the cause is written server-side, where the ids are.
     */
    failure?: { reason: VariationsFailureReason };
}

/**
 * Why the table renders an error rather than rows.
 *
 * `error` is a network or SCAPI failure; the other two mirror {@link VariantSetFailure}'s cases and are
 * catalogue-data problems rather than transport ones.
 */
export type VariationsFailureReason = 'error' | 'master-has-no-variants' | 'variants-did-not-resolve';

/**
 * Projects the master's DECLARED variation attributes onto the set, keeping those it carries values
 * for.
 *
 * The declaration is the candidate set, and this is the decision the module turns on. An attribute the
 * master does not declare is not a dimension of the model however much its values differ across the
 * rows — confirmed with the product owner on 2026-08-13, superseding an earlier rule that read
 * observed variance and ignored declarations entirely. Where a master under-declares, the table shows
 * fewer dimensions than its data varies in, and the fix is the declaration in PIM rather than a rule
 * here.
 *
 * DECLARED ORDER is preserved, not first-seen order: the master states the dimensions in an order, and
 * that order does not shift when an unrelated variant is added. Values still dedupe by FIRST
 * APPEARANCE rather than sorting, for the reason specification rows do — sorting invents a ranking the
 * catalogue does not have.
 *
 * An attribute NO member carries a value for is dropped rather than returned empty: it can head no
 * column and its filter could only offer "any", which narrows nothing.
 *
 * @param variantSet - Every variant of the model; a single-element set for a standalone product
 * @param declaredAttributeIds - The master's variation attribute ids, `c_` prefixed to match the
 *   merchant attributes on the variants
 * @param labels - Optional display names by attribute id, for when the catalogue's own labels are
 *   available; ids fall back to a humanised form of themselves
 * @returns One entry per declared attribute the set carries a value for, in declared order
 */
export function collectDeclaredAttributes(
    variantSet: readonly Product[],
    declaredAttributeIds: readonly string[],
    labels?: ReadonlyMap<string, string>
): VariationColumn[] {
    const collected: VariationColumn[] = [];

    for (const attributeId of declaredAttributeIds) {
        const values = new Set<string>();

        for (const product of variantSet) {
            const value = readSpecificationValue(product, attributeId);

            if (value !== undefined) values.add(value);
        }

        if (!values.size) continue;

        collected.push({
            attributeId,
            label: resolveSpecificationLabel(attributeId, labels?.get(attributeId)),
            values: [...values],
        });
    }

    return collected;
}

/**
 * The declared attributes that actually VARY — the ones that earn a column.
 *
 * An attribute set on some members and absent on others counts as varying only when the values it DOES
 * carry differ. A single value present on half the set and absent on the rest reads the same wherever
 * it appears, so it distinguishes no row from another and heads no column — though it remains a
 * filter, because the master still declares it as a dimension of the model.
 *
 * @param variantSet - Every variant of the model
 * @param declaredAttributeIds - The master's variation attribute ids, `c_` prefixed
 * @param labels - Optional display names by attribute id
 * @returns The varying declared attributes, in declared order
 */
export function deriveVariationColumns(
    variantSet: readonly Product[],
    declaredAttributeIds: readonly string[],
    labels?: ReadonlyMap<string, string>
): VariationColumn[] {
    return collectDeclaredAttributes(variantSet, declaredAttributeIds, labels).filter(
        (column) => column.values.length >= 2
    );
}

/**
 * Picks the column that heads the table.
 *
 * The primary dimension is the differing attribute with the MOST distinct values — the one that comes
 * closest to identifying a row on its own. On every model measured this selects the size: sixteen
 * distinct values across sixteen variants, eight across eight.
 *
 * A solitary declared variation attribute wins over cardinality when one is present among the varying
 * columns. Every column is now declared — the declaration is the candidate set — so this rule fires
 * only where exactly ONE declared attribute varies, which is precisely the case where cardinality has
 * nothing to compare.
 *
 * Ties fall to the first column in declared order rather than to an arbitrary winner, so the choice is
 * stable across renders.
 *
 * @param columns - The varying declared attributes
 * @param declaredVariationAttributeIds - Ids the catalogue declares as variation dimensions, `c_`
 *   prefixed to match
 * @returns The heading column, or `undefined` when nothing varies
 */
export function selectPrimaryDimension(
    columns: readonly VariationColumn[],
    declaredVariationAttributeIds: readonly string[] = []
): VariationColumn | undefined {
    if (!columns.length) return undefined;

    const declared = new Set(declaredVariationAttributeIds);
    const declaredColumns = columns.filter((column) => declared.has(column.attributeId));

    if (declaredColumns.length === 1) return declaredColumns[0];

    return columns.reduce((leader, column) => (column.values.length > leader.values.length ? column : leader));
}

/**
 * Builds the whole table model from the resolved variant set.
 *
 * A set of one — a standalone product, or a master whose resolution degraded — yields no columns and
 * one row, which is the specified behaviour rather than an edge case: there is nothing to distinguish
 * and nothing to filter.
 *
 * @param variantSet - Every variant of the model, in the order the resolver returned them
 * @param declaredVariationAttributeIds - Bare variation attribute ids as the catalogue declares them,
 *   without the `c_` prefix the merchant attributes carry
 * @param labels - Optional display names by attribute id
 * @returns The primary column, the remaining columns, and one row per variant
 */
export function buildVariationsTable(
    variantSet: readonly Product[],
    declaredVariationAttributeIds: readonly string[] = [],
    labels?: ReadonlyMap<string, string>
): VariationsTableModel {
    // The declarations arrive bare (`eye-to-eye-stroke`) while merchant attributes arrive prefixed
    // (`c_eye-to-eye-stroke`). Aligning here keeps the prefix rule in one place.
    const declaredIds = declaredVariationAttributeIds.map((id) => `${MERCHANT_ATTRIBUTE_PREFIX}${id}`);

    // Collected ONCE and narrowed, rather than gathered twice: filters and columns are the same
    // projection read at two thresholds, and two passes could disagree about a value's first
    // appearance and order a dropdown differently from the column beside it.
    const filters = collectDeclaredAttributes(variantSet, declaredIds, labels);
    const columns = filters.filter((column) => column.values.length >= 2);
    const primary = selectPrimaryDimension(columns, declaredIds);
    const secondary = columns.filter((column) => column.attributeId !== primary?.attributeId);
    const ordered = primary ? [primary, ...secondary] : secondary;

    const rows = variantSet.map((product) => {
        const values: Record<string, string | undefined> = {};

        // Row values cover the FILTERS, not just the columns. A single-value declared attribute has no
        // column but does have a filter, and `filterVariationRows` matches on these values — without
        // them every row would read as carrying nothing for that attribute and choosing its one value
        // would empty the table.
        for (const column of [...ordered, ...filters]) {
            values[column.attributeId] = readSpecificationValue(product, column.attributeId);
        }

        return { product, values };
    });

    return { ...(primary ? { primary } : {}), secondary, filters, rows };
}
